import type { WorkflowType } from '@communities/content-management/domain';
import { useCommunityWorkflows } from '@communities/content-management/hooks';
import { Illustration, IllustrationLayout, IllustrationType, SectionHeader } from '@core/components';
import { Badge, BadgeVariant, Card, Text, IconButton, Edit, IconSize, Shimmer } from '@soteria/design-system-ve';
import { useState } from 'react';

import { ApiSettingsDialog } from './ApiSettingsDialog';
import * as styles from './ApiUsageBillingSection.css';
import { BillingDialog } from './BillingDialog';
import { BillingEstimate } from './BillingEstimate';

interface ApiUsageBillingSectionProps {
  communityId: string;
  workflowType: WorkflowType;
  isCommunityAdmin: boolean;
  isSystemAdmin: boolean;
}

export function ApiUsageBillingSection({ communityId, workflowType, isCommunityAdmin, isSystemAdmin }: ApiUsageBillingSectionProps) {
  const [isApiSettingsDialogOpen, setIsApiSettingsDialogOpen] = useState(false);
  const [isBillingDialogOpen, setIsBillingDialogOpen] = useState(false);

  const { workflows, loading, errors } = useCommunityWorkflows(communityId, workflowType);

  const maxTps = workflows?.apiSettings?.maxTps;
  const monthlyBudget = workflows?.apiSettings?.monthlyBudget;
  const costPerAudioHour = workflows?.apiSettings?.costPerAudioHour;
  const clipsPerHour = workflows?.apiSettings?.numberOfClipsPerHour;

  const renderContent = () => {
    if (errors) {
      return (
        <Illustration
          description="Failed to load API usage and billing settings."
          layout={IllustrationLayout.HORIZONTAL}
          title="API Usage & Billing Error"
          type={IllustrationType.ERROR}
        />
      );
    }

    if (loading) {
      return <Shimmer className={styles.loading} />;
    }

    return (
      <div className={styles.container}>
        {/* Max TPS Row (system admin only) */}
        {isSystemAdmin && (
          <div className={styles.settingsRow}>
            <div>
              <Text className={styles.label}>Max TPS</Text>
              <Text className={styles.helperText}>Maximum transactions per second allowed for API requests</Text>
            </div>
            <div className={styles.valueContainer}>
              <Badge text={maxTps?.toString() ?? 'Not configured'} variant={BadgeVariant.Approved} />
              <IconButton aria-label="Edit Max TPS" icon={Edit} iconSize={IconSize.XSmall} onClick={() => setIsApiSettingsDialogOpen(true)} />
            </div>
          </div>
        )}

        {/* Monthly Budget Row */}
        <div className={styles.settingsRow}>
          <div>
            <Text className={styles.label}>Monthly Budget</Text>
            <Text className={styles.helperText}>Maximum monthly cost allocated for API usage</Text>
          </div>
          <div className={styles.valueContainer}>
            <Badge text={`$${(monthlyBudget || 5000).toLocaleString()}`} variant={BadgeVariant.Approved} />
            {isCommunityAdmin && (
              <IconButton aria-label="Edit Monthly Budget" icon={Edit} iconSize={IconSize.XSmall} onClick={() => setIsBillingDialogOpen(true)} />
            )}
          </div>
        </div>

        {/* Audio Hours & TPS Estimates */}
        <BillingEstimate className={styles.estimatesContainer} clipsPerHour={clipsPerHour} costPerAudioHour={costPerAudioHour} monthlyBudget={monthlyBudget} />
      </div>
    );
  };

  return (
    <div>
      <SectionHeader description="Configure API performance limits and monthly budget for your community." title="API Usage & Billing" />

      <Card>{renderContent()}</Card>

      {/* Edit API Settings Dialog */}
      <ApiSettingsDialog
        communityId={communityId}
        isOpen={isApiSettingsDialogOpen}
        maxTps={maxTps}
        workflowType={workflowType}
        onClose={() => setIsApiSettingsDialogOpen(false)}
      />

      {/* Edit Billing Dialog */}
      <BillingDialog
        communityId={communityId}
        isOpen={isBillingDialogOpen}
        workflowType={workflowType}
        workflows={workflows}
        onClose={() => setIsBillingDialogOpen(false)}
      />
    </div>
  );
}
